import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, HeartHandshake } from "lucide-react";
import { getAnnualSponsors } from "../services/api";

const AnnualSponsors = () => {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSponsors = async () => {
      try {
        const data = await getAnnualSponsors();
        setSponsors(data.sponsors || []);
      } catch (err) {
        console.log("Could not fetch annual sponsors (API unavailable)");
      } finally {
        setLoading(false);
      }
    };

    fetchSponsors();
  }, []);

  // Group sponsors by tier, preserving the API's sort order.
  // Untiered sponsors are collected under "Sponsors".
  const tierGroups = sponsors.reduce((groups, sponsor) => {
    const tier = sponsor.tier || "Sponsors";
    const group = groups.find((g) => g.tier === tier);
    if (group) {
      group.sponsors.push(sponsor);
    } else {
      groups.push({ tier, sponsors: [sponsor] });
    }
    return groups;
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="asponsors-page"
    >
      {/* Hero */}
      <section className="asps-hero">
        <div className="asps-overlay" />
        <motion.div
          className="asps-hero-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            The Partners Behind the Movement
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            Our annual sponsors stand with 3E Entertainment year-round —
            powering festivals, artist tours, and community programs that keep
            Nepali culture thriving across the United States.
          </motion.p>
        </motion.div>
      </section>

      {/* Sponsors */}
      <section className="asps-body">
        <div className="asps-container">
          {loading ? (
            <div className="asps-grid">
              {[1, 2, 3, 4, 5, 6].map((item) => (
                <div key={item} className="asps-skeleton asps-shimmer" />
              ))}
            </div>
          ) : sponsors.length === 0 ? (
            <motion.div
              className="asps-empty"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="asps-empty-icon">
                <HeartHandshake size={48} strokeWidth={1.5} />
              </div>
              <h3>Our sponsor family is growing</h3>
              <p>
                Annual sponsors will be featured here soon. Interested in
                supporting a year of cultural programming?
              </p>
              <Link to="/sponsorships" className="asps-cta-btn">
                Become a Sponsor <ArrowRight size={16} />
              </Link>
            </motion.div>
          ) : (
            tierGroups.map((group, groupIndex) => (
              <div key={group.tier} className="asps-tier-block">
                <motion.div
                  className="asps-tier-head"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                  <h2>{group.tier}</h2>
                  <span className="asps-tier-line" />
                </motion.div>
                <div className="asps-grid">
                  {group.sponsors.map((sponsor, index) => (
                    <motion.article
                      key={sponsor.id}
                      className="asps-card"
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.08 }}
                      viewport={{ once: true }}
                    >
                      <div className="asps-card-logo">
                        {sponsor.logo_path ? (
                          <img src={sponsor.logo_path} alt={sponsor.name} loading="lazy" />
                        ) : (
                          <span className="asps-monogram">{sponsor.name.charAt(0)}</span>
                        )}
                      </div>
                      <div className="asps-card-body">
                        <h3>{sponsor.name}</h3>
                        {sponsor.description && <p>{sponsor.description}</p>}
                        {sponsor.website_url && (
                          <a
                            href={sponsor.website_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="asps-card-link"
                          >
                            Visit Website <ExternalLink size={14} />
                          </a>
                        )}
                      </div>
                    </motion.article>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="asps-cta-section">
        <div className="asps-container">
          <motion.div
            className="asps-cta-box"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div>
              <h2>Join Our Annual Sponsor Family</h2>
              <p>
                Put your brand alongside a full year of cultural programming —
                festivals, national tours, and community impact reaching
                thousands across the country.
              </p>
            </div>
            <Link to="/sponsorships" className="asps-cta-btn">
              Become a Sponsor <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      <style>{`
        .asponsors-page { overflow-x: hidden; }

        .asps-hero {
          position: relative;
          min-height: 55vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: url("/images/R5BI6877.webp") center/cover no-repeat;
        }

        .asps-overlay {
          position: absolute;
          inset: 0;
          background: rgba(10, 10, 10, 0.84);
        }

        .asps-hero-content {
          position: relative;
          z-index: 2;
          text-align: center;
          max-width: 800px;
          padding: 7rem 2rem 5rem;
        }


        .asps-hero-content h1 {
          font-size: clamp(2.5rem, 5.5vw, 3.8rem);
          font-weight: 900;
          color: white;
          margin: 0 0 1.5rem;
          line-height: 1.1;
        }

        .asps-hero-content p {
          font-size: 1.15rem;
          color: rgba(255, 255, 255, 0.85);
          line-height: 1.7;
          max-width: 620px;
          margin: 0 auto;
        }

        .asps-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
        }

        .asps-body {
          padding: 6rem 0;
          background: #fff;
        }

        .asps-tier-block + .asps-tier-block {
          margin-top: 4.5rem;
        }

        .asps-tier-head {
          display: flex;
          align-items: center;
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .asps-tier-head h2 {
          font-size: clamp(1.4rem, 2.5vw, 1.9rem);
          font-weight: 900;
          color: #0a0a0a;
          margin: 0;
          text-transform: capitalize;
          white-space: nowrap;
        }

        .asps-tier-line {
          flex: 1;
          height: 2px;
          background: linear-gradient(90deg, #C72631, transparent);
          border-radius: 999px;
        }

        .asps-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.5rem;
        }

        .asps-card {
          display: flex;
          flex-direction: column;
          background: #fff;
          border: 1px solid #e8e8e8;
          border-radius: 16px;
          overflow: hidden;
          transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1), border-color 0.4s ease, box-shadow 0.4s ease;
        }

        .asps-card:hover {
          transform: translateY(-8px);
          border-color: #C72631;
          box-shadow: 0 18px 44px rgba(0, 0, 0, 0.08);
        }

        .asps-card-logo {
          height: 150px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1.75rem;
          background: #fafafa;
          border-bottom: 1px solid #efefef;
        }

        .asps-card-logo img {
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
        }

        .asps-monogram {
          width: 72px;
          height: 72px;
          border-radius: 50%;
          background: #f6f1e9;
          color: #C72631;
          font-size: 2rem;
          font-weight: 900;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .asps-card-body {
          display: flex;
          flex-direction: column;
          flex: 1;
          padding: 1.4rem 1.5rem 1.6rem;
        }

        .asps-card-body h3 {
          font-size: 1.1rem;
          font-weight: 800;
          color: #0a0a0a;
          margin: 0 0 0.5rem;
        }

        .asps-card-body p {
          font-size: 0.88rem;
          color: #666;
          line-height: 1.65;
          margin: 0 0 1rem;
        }

        .asps-card-link {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          color: #C72631;
          font-size: 0.86rem;
          font-weight: 700;
          text-decoration: none;
          margin-top: auto;
          width: fit-content;
          transition: color 0.2s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .asps-card-link:hover {
          color: #0a0a0a;
        }

        .asps-empty {
          background: #f6f1e9;
          border-radius: 16px;
          padding: 4rem 2rem;
          text-align: center;
          border: 1px solid #e8e8e8;
        }

        .asps-empty-icon {
          width: 100px;
          height: 100px;
          border-radius: 50%;
          background: #fff;
          border: 2px solid #e8e8e8;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 2rem;
          color: #C72631;
        }

        .asps-empty h3 {
          font-size: 1.7rem;
          font-weight: 900;
          color: #0a0a0a;
          margin: 0 0 0.75rem;
        }

        .asps-empty p {
          font-size: 1rem;
          color: #666;
          line-height: 1.8;
          max-width: 520px;
          margin: 0 auto 2rem;
        }

        .asps-cta-section {
          padding: 0 0 6rem;
          background: #fff;
        }

        .asps-cta-box {
          background: #0a0a0a;
          border-radius: 20px;
          padding: 3.5rem 4rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 3rem;
        }

        .asps-cta-box h2 {
          font-size: clamp(1.8rem, 3vw, 2.4rem);
          font-weight: 900;
          color: #fff;
          margin: 0 0 1rem;
          line-height: 1.1;
        }

        .asps-cta-box p {
          font-size: 1rem;
          color: rgba(255, 255, 255, 0.65);
          line-height: 1.75;
          margin: 0;
          max-width: 560px;
        }

        .asps-cta-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: #C72631;
          color: #fff;
          padding: 1rem 2rem;
          border-radius: 8px;
          font-size: 0.95rem;
          font-weight: 700;
          text-decoration: none;
          text-transform: uppercase;
          letter-spacing: 1px;
          transition: background 0.3s cubic-bezier(0.22, 1, 0.36, 1), transform 0.3s ease;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .asps-cta-btn:hover {
          background: #a01f28;
          transform: translateY(-3px);
        }

        .asps-skeleton {
          height: 280px;
          border-radius: 16px;
        }

        .asps-shimmer {
          background: #efefef;
          position: relative;
          overflow: hidden;
        }

        .asps-shimmer::after {
          content: "";
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent 0%, #f7f7f7 50%, transparent 100%);
          animation: aspsShimmer 1.8s ease-in-out infinite;
        }

        @keyframes aspsShimmer {
          0% { left: -100%; }
          100% { left: 100%; }
        }

        @media (max-width: 1024px) {
          .asps-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .asps-cta-box {
            flex-direction: column;
            padding: 3rem 2rem;
            text-align: center;
          }
        }

        @media (max-width: 768px) {
          .asps-body {
            padding: 4rem 0;
          }

          .asps-container {
            padding: 0 1.5rem;
          }

          .asps-grid {
            grid-template-columns: 1fr;
            max-width: 460px;
            margin: 0 auto;
          }

          .asps-tier-head h2 {
            white-space: normal;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default AnnualSponsors;
